if (!customElements.get('subscription-modal')) {
  customElements.define(
    'subscription-modal',
    class SubscriptionModal extends HTMLElement {
      constructor() {
        super();
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.productsLoaded = false;
        this.addonsLoaded = false;
        this.selected = []; // box products: [{ id, title, image, priceCents }]
        this.addons = []; // optional extras: [{ id, title, image, priceCents }]
        this.sellingPlanId = null;
        this.step = 'box'; // 'box' | 'addons'
      }

      connectedCallback() {
        // Move the modal to <body> so `position: fixed` resolves against the
        // viewport rather than a transformed/sticky ancestor. Moving re-triggers
        // connectedCallback; the guard runs the real setup only once it's a
        // direct child of <body>.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        document.addEventListener('submit', this.onFormSubmit, true);
        this.addEventListener('click', this.onClick);
        document.addEventListener('keydown', this.onKeydown);
        if (!this.dataset.noPrefill) this.prefillMainProduct();
        this.renderDrawer();

        // Auto-open on load (e.g. the dedicated subscription PDP) so the
        // customer lands straight in the box-builder flow.
        if (this.dataset.autoOpen) this.open();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onFormSubmit, true);
        this.removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeydown);
      }

      /* ---------- getters ---------- */

      get shopifySection() {
        return document.querySelector(`#shopify-section-${this.dataset.sectionId}`);
      }

      get boxGrid() {
        return this.querySelector('[data-subscription-grid]');
      }

      get addonsGrid() {
        return this.querySelector('[data-addons-grid]');
      }

      get boxSize() {
        return parseInt(this.dataset.boxSize, 10) || 2;
      }

      get isBoxFull() {
        return this.selected.length >= this.boxSize;
      }

      get checkedSubscriptionRadio() {
        const checked = this.shopifySection?.querySelector('input[data-radio-type]:checked');
        return checked && checked.dataset.radioType === 'selling_plan' ? checked : null;
      }

      /* ---------- open / close / submit interception ---------- */

      onFormSubmit(event) {
        const form = event.target;
        if (form.dataset.type !== 'add-to-cart-form') return;
        if (!this.shopifySection?.contains(form)) return;

        const radio = this.checkedSubscriptionRadio;
        if (!radio) return; // one-time → let Dawn handle it.

        event.preventDefault();
        event.stopImmediatePropagation();
        this.sellingPlanId = radio.dataset.sellingPlanId || null;
        this.open();
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      }

      open() {
        this.removeAttribute('hidden');
        this.classList.add('is-open');
        document.body.classList.add('overflow-hidden', 'subscription-modal-open');
        // Clear any on-scroll-up "hidden" state so the header sits flush at top.
        document
          .querySelector('.section-header')
          ?.classList.remove('shopify-section-header-hidden');
        if (!this.productsLoaded) this.loadProducts();
      }

      close() {
        this.classList.remove('is-open');
        this.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden', 'subscription-modal-open');
      }

      prefillMainProduct() {
        const id = this.dataset.mainVariantId;
        if (!id || this.selected.some((item) => item.id === id)) return;
        this.selected.push({
          id,
          title: this.dataset.mainTitle || '',
          image: this.dataset.mainImage || '',
          priceCents: parseInt(this.dataset.mainPrice, 10) || 0,
        });
      }

      /* ---------- clicks ---------- */

      onClick(event) {
        if (event.target.closest('[data-subscription-close]')) return this.close();
        if (event.target.closest('[data-drawer-back]')) return this.goToBox();

        const stepUp = event.target.closest('[data-step-up]');
        if (stepUp) return this.addProduct(stepUp.closest('[data-stepper]'));

        const stepDown = event.target.closest('[data-step-down]');
        if (stepDown) return this.removeOneProduct(stepDown.closest('[data-stepper]').dataset.variantId);

        const removeBox = event.target.closest('[data-remove-from-box]');
        if (removeBox) return this.removeProductAt(Number(removeBox.dataset.slotIndex));

        const addAddon = event.target.closest('[data-add-addon]');
        if (addAddon) return this.toggleAddon(addAddon);

        const removeAddon = event.target.closest('[data-remove-addon]');
        if (removeAddon) {
          this.addons = this.addons.filter((item) => item.id !== removeAddon.dataset.variantId);
          this.syncCardButtons();
          this.renderDrawer();
          return;
        }

        if (event.target.closest('[data-drawer-primary]')) {
          if (this.step === 'box') {
            if (this.isBoxFull) this.goToAddons();
          } else {
            this.checkout();
          }
        }
      }

      /* ---------- steps ---------- */

      goToAddons() {
        if (!this.isBoxFull) return;
        this.step = 'addons';
        this.boxGrid.hidden = true;
        this.addonsGrid.hidden = false;
        this.updateHeader();
        if (!this.addonsLoaded) this.loadAddons();
        this.renderDrawer();
        this.scrollTop = 0;
      }

      goToBox() {
        this.step = 'box';
        this.addonsGrid.hidden = true;
        this.boxGrid.hidden = false;
        this.updateHeader();
        this.renderDrawer();
        this.scrollTop = 0;
      }

      // Swap the heading + subheading for the current step. The addons step has
      // no subheading by default, so it's hidden there.
      updateHeader() {
        const title = this.querySelector('[data-modal-title]');
        const subtitle = this.querySelector('[data-modal-subtitle]');
        const inAddons = this.step === 'addons';

        if (title) {
          title.textContent = inAddons
            ? this.dataset.addonsHeading || 'Add subscription essentials'
            : this.dataset.boxHeading || 'You’re about to become a Maniac Member';
        }
        if (subtitle) {
          const text = inAddons ? this.dataset.addonsSubheading || '' : this.dataset.boxSubheading || '';
          subtitle.textContent = text;
          subtitle.hidden = !text;
        }
      }

      /* ---------- box selection ---------- */

      // Add another copy of a product to the box (the stepper's "+"). The same
      // product can be added multiple times, up to the box size.
      addProduct(button) {
        if (this.isBoxFull) return;
        this.selected.push(this.itemFromButton(button));
        this.syncCardButtons();
        this.renderDrawer();
      }

      // Remove one copy of a product (the stepper's "−"). Count never drops below 0.
      removeOneProduct(id) {
        const index = this.selected.findIndex((item) => item.id === id);
        if (index === -1) return;
        this.selected.splice(index, 1);
        this.syncCardButtons();
        this.renderDrawer();
      }

      // Remove a single box copy by its slot position, so duplicates of the same
      // product can be removed one at a time rather than all at once.
      removeProductAt(index) {
        if (Number.isNaN(index) || index < 0 || index >= this.selected.length) return;
        this.selected.splice(index, 1);
        this.syncCardButtons();
        this.renderDrawer();
      }

      /* ---------- addons selection ---------- */

      toggleAddon(button) {
        const id = button.dataset.variantId;
        if (this.addons.some((item) => item.id === id)) {
          this.addons = this.addons.filter((item) => item.id !== id);
        } else {
          this.addons.push(this.itemFromButton(button));
        }
        this.syncCardButtons();
        this.renderDrawer();
      }

      itemFromButton(button) {
        return {
          id: button.dataset.variantId,
          title: button.dataset.title || '',
          image: button.dataset.image || '',
          priceCents: parseInt(button.dataset.priceCents, 10) || 0,
        };
      }

      // Reflect selection + full state on grid buttons (both box and addons).
      syncCardButtons() {
        const full = this.isBoxFull;
        this.boxGrid?.querySelectorAll('[data-stepper]').forEach((stepper) => {
          const count = this.selected.filter((item) => item.id === stepper.dataset.variantId).length;
          const countEl = stepper.querySelector('[data-count]');
          if (countEl) countEl.textContent = count;
          stepper.classList.toggle('is-added', count > 0);

          // "−" is disabled at 0 (never goes lower); "+" is disabled once this
          // product hits the box size or the box is full overall (never higher).
          const down = stepper.querySelector('[data-step-down]');
          const up = stepper.querySelector('[data-step-up]');
          if (down) down.disabled = count <= 0;
          if (up) up.disabled = full || count >= this.boxSize;
        });
        this.addonsGrid?.querySelectorAll('[data-add-addon]').forEach((button) => {
          const isSelected = this.addons.some((item) => item.id === button.dataset.variantId);
          button.classList.toggle('is-added', isSelected);
          button.textContent = isSelected ? 'Remove' : '+ Add';
        });
      }

      /* ---------- drawer ---------- */

      renderDrawer() {
        const slots = this.querySelector('[data-drawer-slots]');
        const text = this.querySelector('[data-drawer-text]');
        const total = this.querySelector('[data-drawer-total]');
        const compare = this.querySelector('[data-drawer-compare]');
        const primary = this.querySelector('[data-drawer-primary]');
        const back = this.querySelector('[data-drawer-back]');
        if (!slots) return;

        const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        const inAddons = this.step === 'addons';

        // Box slots (filled + empty placeholders).
        slots.innerHTML = '';
        for (let i = 0; i < this.boxSize; i += 1) {
          const item = this.selected[i];
          const li = document.createElement('li');
          li.className = 'subscription-drawer__slot';
          if (item) {
            li.innerHTML = `
              ${item.image ? `<img src="${item.image}" alt="${this.escape(item.title)}">` : ''}
              <button type="button" class="subscription-drawer__slot-remove" data-remove-from-box data-slot-index="${i}" data-variant-id="${item.id}" aria-label="Remove ${this.escape(item.title)}">&times;</button>
            `;
          }
          slots.appendChild(li);
        }

        // Selected addons (extras) row — visible whenever any are picked.
        const addonsWrap = this.querySelector('[data-drawer-addons-wrap]');
        const addonsList = this.querySelector('[data-drawer-addons]');
        if (addonsList) {
          addonsList.innerHTML = '';
          this.addons.forEach((item) => {
            const li = document.createElement('li');
            li.className = 'subscription-drawer__slot';
            li.innerHTML = `
              ${item.image ? `<img src="${item.image}" alt="${this.escape(item.title)}">` : ''}
              <button type="button" class="subscription-drawer__slot-remove" data-remove-addon data-variant-id="${item.id}" aria-label="Remove ${this.escape(item.title)}">&times;</button>
            `;
            addonsList.appendChild(li);
          });
          if (addonsWrap) addonsWrap.hidden = this.addons.length === 0;
        }

        // Progress / status text.
        if (text) {
          if (inAddons) {
            const n = this.addons.length;
            const _extrasTmpl = n === 1 ? this.dataset.extrasAddedOne : this.dataset.extrasAddedOther;
            text.textContent = n ? (_extrasTmpl ? _extrasTmpl.replace('%count%', n) : `${n} extra${n === 1 ? '' : 's'} added`) : (this.dataset.addExtras || 'Add optional extras');
          } else {
            const remaining = this.boxSize - this.selected.length;
            const _moreTmpl = remaining === 1 ? this.dataset.chooseMoreOne : this.dataset.chooseMoreOther;
            text.textContent = this.isBoxFull
              ? (this.dataset.boxComplete || 'Your box is complete')
              : (_moreTmpl ? _moreTmpl.replace('%count%', remaining) : `Choose ${remaining} more ${remaining === 1 ? 'product' : 'products'}`);
          }
        }

        // Prices: subscription box price (fixed if set) + the chosen extras,
        // with a struck-through "regular" compare (box regular prices + extras).
        const boxRegularCents = this.selected.reduce((acc, item) => acc + item.priceCents, 0);
        const addonsCents = this.addons.reduce((acc, item) => acc + item.priceCents, 0);
        const fixedPrice = this.dataset.boxPrice;
        const boxBaseCents = fixedPrice ? this.parsePriceToCents(fixedPrice) : boxRegularCents;

        const totalCents = boxBaseCents + addonsCents;
        const compareCents = boxRegularCents + addonsCents;

        if (total) total.textContent = this.formatMoney(totalCents, moneyFormat);
        if (compare) {
          compare.textContent = compareCents > totalCents ? this.formatMoney(compareCents, moneyFormat) : '';
        }

        // Primary button: label + enabled state per step.
        if (primary) {
          primary.textContent = inAddons
            ? this.dataset.checkoutLabel || 'Check out'
            : this.dataset.nextLabel || 'Next';
          primary.disabled = inAddons ? false : !this.isBoxFull;
        }
        if (back) back.hidden = !inAddons;
      }

      /* ---------- checkout ---------- */

      async checkout() {
        if (!this.isBoxFull) return;

        const primary = this.querySelector('[data-drawer-primary]');
        primary?.setAttribute('disabled', 'disabled');

        // Shared id so the box items act as one all-or-nothing combo in the cart.
        const comboId = `combo-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
        const sellingPlan = this.checkedSubscriptionRadio ? Number(this.checkedSubscriptionRadio.dataset.sellingPlanId) : null;
        const items = [
          // The two box products are subscription lines, grouped by _combo_id.
          ...this.selected.map((item, index) => ({
            id: Number(item.id),
            quantity: 1,
            selling_plan: sellingPlan,
            // Different properties keep identical variants on separate cart/order
            // lines, so the subscription portal can swap each design individually.
            properties: { _combo_id: comboId, _subscription_slot: String(index + 1) },
          })),
          // Optional extras are added as normal one-time lines.
          ...this.addons.map((item) => ({ id: Number(item.id), quantity: 1 })),
        ];

        try {
          const response = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });
          const data = await response.json();
          if (data.status) {
            console.error('Subscription checkout failed:', data.description || data.message);
            primary?.removeAttribute('disabled');
            return;
          }
          window.location = '/checkout';
        } catch (error) {
          console.error(error);
          primary?.removeAttribute('disabled');
        }
      }

      /* ---------- product loading ---------- */

      loadProducts() {
        return this.fetchInto(this.dataset.collectionHandle, this.boxGrid, 'box', true);
      }

      loadAddons() {
        this.addonsLoaded = true;
        return this.fetchInto(this.dataset.addonsHandle, this.addonsGrid, 'addon', false);
      }

      // Fetch a collection (paginated, in-stock only) and render cards into a grid.
      // applyTag: only used for the box step.
      async fetchInto(handle, grid, action, applyTag) {
        if (action === 'box') this.productsLoaded = true;
        this.classList.add('is-loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const products = [];
        const seen = new Set();
        let failed = false;

        try {
          // Page through until a page returns no NEW products. Don't assume a
          // fixed page size — the endpoint may return 50 per page even when a
          // larger limit is requested, so breaking on "< 250" would stop early.
          for (let page = 1; page <= 100; page += 1) {
            let data = null;

            // Reuse the inline prefetch fired during HTML parse for the first
            // page of the box collection (the auto-opened subscription PDP).
            if (page === 1 && action === 'box' && window.__maniacSubPrefetch) {
              data = await window.__maniacSubPrefetch;
              window.__maniacSubPrefetch = null;
            }

            if (!data) {
              const response = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
              if (!response.ok) {
                if (response.status !== 404) failed = true;
                else console.error(`Subscription modal: collection "${handle}" not found (404).`);
                break;
              }
              data = await response.json();
            }

            if (!data.products || data.products.length === 0) break;

            let added = 0;
            data.products.forEach((product) => {
              if (seen.has(product.id)) return;
              seen.add(product.id);
              products.push(product);
              added += 1;
            });
            if (added === 0) break; // no new products (empty/repeated page) → done.
          }

          const inStock = products.filter((product) => product.variants?.some((v) => v.available));
          const filtered = applyTag ? this.filterByTag(inStock) : inStock;
          this.renderProducts(filtered, grid, action);

          if (filtered.length === 0) {
            grid.innerHTML = `<li class="subscription-modal__message">No products found for collection “${this.escape(
              handle
            )}”.</li>`;
          }
        } catch (error) {
          console.error('Subscription modal: failed to load products', error);
          failed = true;
        } finally {
          this.classList.remove('is-loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }

        if (failed) {
          if (action === 'box') this.productsLoaded = false;
          else this.addonsLoaded = false;
        }
      }

      renderProducts(products, grid, action) {
        const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        const fragment = document.createDocumentFragment();

        products.forEach((product) => {
          const variant = (product.variants && product.variants[0]) || {};
          const image = product.images && product.images[0] ? this.sizedImage(product.images[0].src, 600) : '';
          const priceCents = Math.round(parseFloat(variant.price || 0) * 100);
          const dataAttrs = `
            data-variant-id="${variant.id}"
            data-title="${this.escape(product.title)}"
            data-image="${image}"
            data-price-cents="${priceCents}"`;

          // Box cards use a −/+ stepper (0…box size); addon cards keep a single
          // add/remove toggle button.
          const control =
            action === 'addon'
              ? `<button type="button" class="subscription-card__button button button--full-width button--secondary" data-add-addon ${dataAttrs}>${this.escape(this.dataset.addExtraLabel || '+ Add')}</button>`
              : `<div class="subscription-card__stepper" data-stepper ${dataAttrs}>
                   <button type="button" class="subscription-card__step" data-step-down aria-label="${this.escape('Remove one ' + product.title)}">&minus;</button>
                   <span class="subscription-card__count" data-count aria-live="polite">0</span>
                   <button type="button" class="subscription-card__step" data-step-up aria-label="${this.escape('Add one ' + product.title)}">+</button>
                 </div>`;

          const li = document.createElement('li');
          li.className = 'subscription-card';
          li.innerHTML = `
            <span class="subscription-card__image-wrapper">
              ${image ? `<img class="subscription-card__image" src="${image}" alt="${this.escape(product.title)}" width="600" height="600" loading="lazy">` : ''}
            </span>
            <p class="subscription-card__title">${this.escape(product.title)}</p>
            <p class="subscription-card__price">${this.formatMoney(priceCents, moneyFormat)}</p>
            ${control}
          `;
          fragment.appendChild(li);
        });

        grid.appendChild(fragment);
        this.syncCardButtons();
      }

      /* ---------- helpers ---------- */

      filterByTag(products) {
        const tag = (this.dataset.requiredTag || '').trim().toLowerCase();
        if (!tag) return products;
        return products.filter((product) => {
          const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
          return tags.some((t) => t.trim().toLowerCase() === tag);
        });
      }

      sizedImage(src, width) {
        const separator = src.includes('?') ? '&' : '?';
        return `${src}${separator}width=${width}`;
      }

      escape(value) {
        const div = document.createElement('div');
        div.textContent = value == null ? '' : value;
        return div.innerHTML;
      }

      formatMoney(cents, format) {
        const number = (cents / 100).toFixed(2);
        const parts = number.split('.');
        const thousandsDot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const thousandsComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        const values = {
          amount: `${thousandsComma}.${parts[1]}`,
          amount_no_decimals: thousandsComma,
          amount_with_comma_separator: `${thousandsDot},${parts[1]}`,
          amount_no_decimals_with_comma_separator: thousandsDot,
          amount_with_period_separator: `${thousandsComma}.${parts[1]}`,
        };

        const formatted = format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) =>
          values[key] != null ? values[key] : values.amount
        );
        return this.tidyPrice(formatted);
      }

      // Parse a money string (e.g. "€30,-", "€1.234,50") into integer cents.
      parsePriceToCents(str) {
        if (str == null) return 0;
        // "30,-" / "30.-" mean a whole amount (00 cents).
        let s = String(str).replace(/([.,])-\s*$/, '$100');
        s = s.replace(/[^\d.,]/g, '');
        if (!s) return 0;

        const lastComma = s.lastIndexOf(',');
        const lastDot = s.lastIndexOf('.');
        const decimalSep = lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : '';
        if (decimalSep) {
          const thousandsSep = decimalSep === ',' ? '.' : ',';
          s = s.split(thousandsSep).join('').replace(decimalSep, '.');
        }
        const value = parseFloat(s);
        return isNaN(value) ? 0 : Math.round(value * 100);
      }

      // Show whole amounts as ",-" instead of ",00" (e.g. €10,00 → €10,-).
      tidyPrice(str) {
        return String(str).replace(/,00(?=\D|$)/, ',-');
      }
    }
  );
}
